/**
 * The composer's staged attachment lists that localStorage refused to hold.
 *
 * Emails and brand images staged on a chat's composer are mirrored to a
 * per-chat localStorage key (`use-chat-attached-emails.ts`,
 * `use-chat-attached-images.ts`), and a chat reads its list back from that
 * key every time it becomes the active chat. When storage refuses a write
 * (quota exceeded, storage disabled), the list is kept here instead, so the
 * chat reads back what it holds rather than an older list or nothing. "New
 * chat" on the full page needs this: until its navigation lands the old
 * chat is active again, so the new chat is read back a second time.
 *
 * A kept list is this page's edits on top of its BASE: what storage held
 * when this page last read or stored the key, which is what its list was
 * derived from. Another tab may write the key meanwhile, so a read replays
 * this page's edits onto what storage holds NOW (`rebaseOnStorage`): both
 * tabs' changes survive, with no event ordering or clock involved. A deleted
 * brand image leaves every tab's kept lists for good (`dropFromUnsavedLists`),
 * whatever storage still holds.
 *
 * In memory on purpose: it lasts as long as the page. A later write that
 * storage accepts drops the entry, and at most `MAX_UNSAVED_LISTS` are kept,
 * never dropping an open chat's.
 */
type KeptList = {
  /** This page's list. */
  list: ReadonlyArray<unknown>
  /** What storage held for the key when this page derived `list`. */
  base: string | null
  /**
   * An entry's identity (a URL, an email id), to line lists up by;
   * `undefined` for an entry that is not a valid one.
   */
  identify: (entry: unknown) => string | undefined
  /** Deleted brand images, never read back from storage. */
  dropped: ReadonlySet<string>
}

const unsavedLists = new Map<string, KeptList>()

/**
 * What storage held for a key when this page last read or stored it: the
 * base of the list the page derives. Bounded like the lists (a key whose
 * entry is gone falls back to what storage holds at the refused write).
 */
const observed = new Map<string, string | null>()
const MAX_OBSERVED_KEYS = 256

/**
 * Bound on kept lists, least recently written dropped first. An open chat's
 * lists are never dropped (the uploads store's rule), so the map exceeds it
 * only by open chats; a dropped chat reads storage again (its last list
 * storage accepted).
 */
export const MAX_UNSAVED_LISTS = 64

/** The app's tabs tell each other about deleted images storage cannot carry. */
export const UNSAVED_LISTS_CHANNEL = 'brew-unsaved-attachment-lists'

type DropMessage = { prefix: string; id: string }

const NO_CHAT_IS_OPEN = () => false
let isChatOpen: (chatId: string) => boolean = NO_CHAT_IS_OPEN
let channel: BroadcastChannel | null = null

/**
 * Which chats are open (the strip's `isChatOpen`, registered by the chat
 * provider). Browser-only: a server render must not hold a request's state.
 */
export function retainUnsavedListsOfOpenChats(
  isOpen: (chatId: string) => boolean
): void {
  if (typeof window === 'undefined') {
    return
  }
  isChatOpen = isOpen
}

/** Attachment keys end with `:<chatId>` (chat ids are nanoids). */
function chatIdOfKey(key: string): string {
  return key.slice(key.lastIndexOf(':') + 1)
}

function readStoredRaw(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function noteObserved(key: string, raw: string | null): void {
  observed.delete(key)
  observed.set(key, raw)
  for (const oldest of observed.keys()) {
    if (observed.size <= MAX_OBSERVED_KEYS) {
      break
    }
    observed.delete(oldest)
  }
}

/**
 * Read `key` from storage (`null` when missing or unreadable), noting it as
 * the base of the list this page derives from it. Browser-only.
 */
export function readStored(key: string): string | null {
  const raw = readStoredRaw(key)
  noteObserved(key, raw)
  return raw
}

function byId(
  list: ReadonlyArray<unknown>,
  identify: KeptList['identify']
): Map<string, unknown> {
  const entries = new Map<string, unknown>()
  for (const entry of list) {
    const id = identify(entry)
    if (id) {
      entries.set(id, entry)
    }
  }
  return entries
}

function parseById(
  raw: string | null,
  identify: KeptList['identify']
): Map<string, unknown> {
  if (!raw) {
    return new Map()
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? byId(parsed, identify) : new Map()
  } catch {
    // Not a list this app wrote: nothing in it.
    return new Map()
  }
}

/**
 * This page's edits (against `kept.base`) replayed onto `stored`, what
 * storage holds now. This page's entries come first, in its order: its own
 * version of one it added or changed, storage's version of the rest, and
 * none it did not change but storage no longer holds. Then what another tab
 * added: stored entries neither in this page's list nor in its base (one in
 * the base but not the list was removed here) and not deleted images. So
 * another tab's additions, removals and later edits show, this page's edits
 * win where both tabs changed an entry, and a read cap (the image list's)
 * keeps this page's own chips.
 */
function rebaseOnStorage(
  kept: KeptList,
  stored: string | null
): Array<unknown> {
  const base = parseById(kept.base, kept.identify)
  const ours = byId(kept.list, kept.identify)
  const theirs = parseById(stored, kept.identify)
  const isChangedHere = (id: string) =>
    !base.has(id) ||
    JSON.stringify(ours.get(id)) !== JSON.stringify(base.get(id))
  const list: Array<unknown> = []
  for (const [id, entry] of ours) {
    if (theirs.has(id)) {
      list.push(isChangedHere(id) ? entry : theirs.get(id))
    } else if (isChangedHere(id)) {
      list.push(entry)
    }
  }
  for (const [id, entry] of theirs) {
    if (!(ours.has(id) || base.has(id) || kept.dropped.has(id))) {
      list.push(entry)
    }
  }
  return list
}

function dropHere({ prefix, id }: DropMessage): void {
  for (const [key, kept] of unsavedLists) {
    if (key.startsWith(prefix)) {
      unsavedLists.set(key, {
        ...kept,
        list: kept.list.filter((entry) => kept.identify(entry) !== id),
        dropped: new Set([...kept.dropped, id]),
      })
    }
  }
}

function isDropMessage(value: unknown): value is DropMessage {
  const message = value as Partial<DropMessage> | null
  return (
    typeof message?.prefix === 'string' &&
    message.prefix.length > 0 &&
    typeof message.id === 'string' &&
    message.id.length > 0
  )
}

function openChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') {
    return channel
  }
  channel = new BroadcastChannel(UNSAVED_LISTS_CHANNEL)
  channel.onmessage = (event: MessageEvent<unknown>) => {
    if (isDropMessage(event.data)) {
      dropHere(event.data)
    }
  }
  return channel
}

/**
 * The list for `key` when this page keeps one (its edits replayed onto what
 * storage holds now, which becomes its base), else `undefined`: read storage
 * with `readStored`.
 */
export function readUnsavedList<T>(key: string): Array<T> | undefined {
  const kept = unsavedLists.get(key)
  if (!kept) {
    return undefined
  }
  const stored = readStored(key)
  return (
    stored === kept.base ? [...kept.list] : rebaseOnStorage(kept, stored)
  ) as Array<T>
}

/**
 * Store `list` under `key` (an empty list removes the key), or keep it in
 * memory when storage refuses. Browser-only; callers guard the server.
 */
export function writeAttachmentList(
  key: string,
  list: ReadonlyArray<unknown>,
  identify: KeptList['identify']
): void {
  try {
    if (list.length === 0) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(list))
    }
    unsavedLists.delete(key)
    noteObserved(key, list.length === 0 ? null : JSON.stringify(list))
  } catch {
    // Quota exceeded or storage disabled: this page keeps the list, as the
    // most recently written, against the base it was derived from. Deleted
    // images stay dropped: `dropped` only hides stored entries this page's
    // list does not hold, so one attached here again still shows.
    const previous = unsavedLists.get(key)
    unsavedLists.delete(key)
    unsavedLists.set(key, {
      list: [...list],
      base: observed.has(key)
        ? (observed.get(key) ?? null)
        : readStoredRaw(key),
      identify,
      dropped: previous?.dropped ?? new Set(),
    })
    for (const oldest of [...unsavedLists.keys()]) {
      if (unsavedLists.size <= MAX_UNSAVED_LISTS) {
        break
      }
      if (oldest !== key && !isChatOpen(chatIdOfKey(oldest))) {
        unsavedLists.delete(oldest)
      }
    }
    openChannel()
  }
}

/**
 * Remove the entry `id` from every kept list under `prefix`, for good, in
 * this page and in the app's other tabs: one only a tab's kept list holds was
 * never stored, and storage may be unable to drop one it holds. For a deleted
 * brand image.
 */
export function dropFromUnsavedLists(prefix: string, id: string): void {
  const message: DropMessage = { prefix, id }
  dropHere(message)
  try {
    openChannel()?.postMessage(message)
  } catch {
    // A closed or unavailable channel: other tabs keep their lists.
  }
}

/**
 * Tests only: forget every unsaved list, what was observed, the open-chat
 * check and the channel.
 */
export function resetUnsavedAttachmentListsForTests(): void {
  unsavedLists.clear()
  observed.clear()
  isChatOpen = NO_CHAT_IS_OPEN
  channel?.close()
  channel = null
}
